



























































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { BugTrackerCategoryViewObject, BugTrackerIssueViewObject, BugTrackerPriority, BugTrackerStatus } from '@/types/apiServerContract';
import urlHelperService from '@/services/url/urlHelper.service';
import { RealmsModule } from '@/store/modules/realms/realms.store';
import scrollService from '@/core/scroll/scroll.service';
import bugtrackerService from './bugtracker.service';
import Paginator from '@/components/paging/Paginator.vue';
import { BugTrackerModule } from '@/store/modules/bugtracker/bugtracker.store';
import { UserModule } from '@/store/modules/user/user.store';
import { User } from '@/models/user/user.model';

type SearchMode = 'simple' | 'advanced';

@Component({
    components: { Paginator }
})
export default class BugTrackerSearch extends Vue {
    @Prop({ required: true, type: Array })
    categories!: BugTrackerCategoryViewObject[];

    term: string = '';
    categoryId: number | null = null;
    onlyOpenIssues: boolean | null = null;
    onlyOwnIssues: boolean | null = null;
    assignedToSelf: boolean | null = null;
    startDate: Date | null = null;
    endDate: Date | null = null;
    status: BugTrackerStatus | null = null;
    priority: BugTrackerPriority | null = null;
    realmId: number | null = null;
    page: number | null = null;
    loading: boolean = false;

    get issues(): BugTrackerIssueViewObject[] {
        return BugTrackerModule.searchResults;
    }

    fields: any = [
	    { key: 'id', label: 'ID', sortable: true },
	    { key: 'title', label: 'Title', sortable: true, class: 'w-30' },
	    { key: 'categoryId', label: 'Category', sortable: true },
	    { key: 'status', label: 'Status', sortable: true, class: 'w-15' },
	    { key: 'priority', label: 'Priority', sortable: true },
	    { key: 'updated', label: 'Updated', sortable: true }
    ];

    mode: SearchMode = 'simple';

    get isInSimpleMode() {
        return this.mode === 'simple';
    }

    get isInAdvancedmode() {
        return this.mode === 'advanced';
    }

    get isLoggedIn() {
        return UserModule.IsLoggedIn;
    }

    get user(): User | null {
	    return UserModule.user;
    }

    get isAdmin() {
	    return this.user?.IsAdmin ?? false;
    }

    gotoDetails(issue: BugTrackerIssueViewObject) {
        this.$router.push('/bugtracker/issue/' + issue.id);
    }

    getCategory(category: number) {
        return this.categories.find(x => x.id === category)!.name;
    }

    getPriority(priority: BugTrackerPriority) {
        return bugtrackerService.getPriority(priority);
    }

    getPriorityColor(priority: BugTrackerPriority) {
        return bugtrackerService.getPriorityColor(priority);
    }

    getStatus(status: BugTrackerStatus) {
        return bugtrackerService.getStatus(status);
    }

    getStatusColor(status: BugTrackerStatus) {
        return bugtrackerService.getStatusColor(status);
    }

    reset() {
        this.categoryId = null;
        this.onlyOpenIssues = null;
        this.onlyOwnIssues = null;
        this.assignedToSelf = null;
        this.realmId = null;
        this.status = null;
        this.priority = null;
        this.page = null;
        this.startDate = null;
        this.endDate = null;
        this.term = '';
    }

    selectMode(mode: SearchMode) {
        this.mode = mode;
        urlHelperService.setQueryParam('mode', mode);

        if (mode === 'simple') {
            this.reset();
            this.synchronizeUrl();
        }
    }

    get realms() {
	    return RealmsModule.realms;
    }

    get bugTrackerStatusTypes() {
        const entries = Object.entries(BugTrackerStatus);
	    return entries.slice(-entries.length / 2).map(x => ({
	        name: bugtrackerService.getStatus(x[1] as BugTrackerStatus),
	        id: x[1]
	    }));
    }

    get bugTrackerPriorityTypes() {
        const entries = Object.entries(BugTrackerPriority);
	    return entries.slice(-entries.length / 2).map(x => ({
	        name: x[0].replace('BugTrackerPriority', ''),
	        id: x[1]
	    }));
    }

    async search() {
        this.loading = true;

        this.synchronizeUrl();

        try {
            await BugTrackerModule.search({
                onlyOpenIssues: this.onlyOpenIssues,
                onlyOwnIssues: this.onlyOwnIssues,
                assignedToSelf: this.assignedToSelf,
                term: this.term,
                categoryId: this.categoryId,
                realmId: this.realmId,
                startDate: this.startDate,
                endDate: this.endDate,
                status: this.status,
                priority: this.priority,
                page: this.page
            });
        } finally {
            this.loading = false;
        }
    }

    synchronizeUrl() {
        urlHelperService.setQueryParam('term', this.term);
        urlHelperService.setQueryParam('categoryId', this.categoryId);
        urlHelperService.setQueryParam('startDate', this.startDate);
        urlHelperService.setQueryParam('endDate', this.endDate);
        urlHelperService.setQueryParam('status', this.status);
        urlHelperService.setQueryParam('priority', this.priority);
        urlHelperService.setQueryParam('realmId', this.realmId);
        urlHelperService.setQueryParam('onlyOpenIssues', this.onlyOpenIssues || null);
        urlHelperService.setQueryParam('onlyOwnIssues', this.onlyOwnIssues || null);
        urlHelperService.setQueryParam('assignedToSelf', this.assignedToSelf || null);
        urlHelperService.setQueryParam('page', this.page);
    }

    synchronizeFromUrl() {
        let foundFilter = false;

        const term = urlHelperService.getQueryParam<string>('term');
        if (term) {
            this.term = term;
            foundFilter = true;
        }
        const categoryId = urlHelperService.getQueryParam<string>('categoryId');
        if (categoryId) {
            this.categoryId = parseInt(categoryId);
            foundFilter = true;
        }
        const startDate = urlHelperService.getQueryParam<string>('startDate');
        if (startDate) {
            this.startDate = new Date(startDate);
            foundFilter = true;
        }
        const endDate = urlHelperService.getQueryParam<string>('endDate');
        if (endDate) {
            this.endDate = new Date(endDate);
            foundFilter = true;
        }
        const status = urlHelperService.getQueryParam<string>('status');
        if (status) {
            this.status = parseInt(status);
            foundFilter = true;
        }
        const priority = urlHelperService.getQueryParam<string>('priority');
        if (priority) {
            this.priority = parseInt(priority);
            foundFilter = true;
        }
        const realmId = urlHelperService.getQueryParam<string>('realmId');
        if (realmId) {
            this.realmId = parseInt(realmId);
            foundFilter = true;
        }
        const page = urlHelperService.getQueryParam<string>('page');
        if (page) {
            this.page = parseInt(page);
            foundFilter = true;
        }
        const onlyOpenIssues = urlHelperService.getQueryParam<string>('onlyOpenIssues');
        if (onlyOpenIssues) {
            this.onlyOpenIssues = Boolean(onlyOpenIssues);
            foundFilter = true;
        }
        const onlyOwnIssues = urlHelperService.getQueryParam<string>('onlyOwnIssues');
        if (onlyOwnIssues) {
            this.onlyOwnIssues = Boolean(onlyOwnIssues);
            foundFilter = true;
        }
        const assignedToSelf = urlHelperService.getQueryParam<string>('assignedToSelf');
        if (assignedToSelf) {
            this.assignedToSelf = Boolean(assignedToSelf);
            foundFilter = true;
        }

        if (foundFilter) {
            this.search();
        }
    }

    created() {
        this.synchronizeFromUrl();

        const mode = urlHelperService.getQueryParam<string>('mode');
        if (mode) {
            this.mode = mode as SearchMode;
        }
    }

    mounted() {
        const element = document.getElementById('main-content');
        if (element) {
            scrollService.scrollToElement(element, -200);
        }
    }

    destroyed() {
        BugTrackerModule.clearSearchResults();
    }
}
